import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Sparkles, Check, Trash2, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => n == null ? '—' : (Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CAMPI = [
  { key: 'nome_dipendente', label: 'Nome dipendente', type: 'text' },
  { key: 'codice_fiscale_dipendente', label: 'Codice fiscale', type: 'text' },
  { key: 'matricola', label: 'Matricola', type: 'text' },
  { key: 'qualifica', label: 'Qualifica', type: 'text' },
  { key: 'periodo.mese', label: 'Mese', type: 'number' },
  { key: 'periodo.anno', label: 'Anno', type: 'number' },
  { key: 'data_pagamento', label: 'Data pagamento', type: 'date' },
  { key: 'importo_lordo', label: 'Importo lordo', type: 'euro' },
  { key: 'importo_netto', label: 'Importo netto', type: 'euro' },
  { key: 'contributi_inps_dipendente', label: 'Contributi INPS dip.', type: 'euro' },
  { key: 'contributi_inps_azienda', label: 'Contributi INPS az.', type: 'euro' },
  { key: 'ritenute_irpef', label: 'Ritenute IRPEF', type: 'euro' },
  { key: 'ritenute_addizionali_regionali', label: 'Add. regionali', type: 'euro' },
  { key: 'ritenute_addizionali_comunali', label: 'Add. comunali', type: 'euro' },
  { key: 'ore_lavorate_ordinarie', label: 'Ore ordinarie', type: 'number' },
  { key: 'ore_lavorate_straordinarie', label: 'Ore straordinarie', type: 'number' },
  { key: 'giorni_lavorati', label: 'Giorni lavorati', type: 'number' },
  { key: 'ferie_residue_giorni', label: 'Ferie residue (gg)', type: 'number' },
  { key: 'permessi_residui_ore', label: 'Permessi residui (h)', type: 'number' },
  { key: 'trasferte_e_diarie', label: 'Trasferte/diarie', type: 'euro' },
  { key: 'tfr_maturato_periodo', label: 'TFR periodo', type: 'euro' },
  { key: 'tfr_maturato_progressivo', label: 'TFR progressivo', type: 'euro' },
];

function getDeep(obj, path) {
  return path.split('.').reduce((acc, p) => (acc && acc[p] != null ? acc[p] : null), obj);
}
function setDeep(obj, path, value) {
  const parts = path.split('.');
  const clone = JSON.parse(JSON.stringify(obj || {}));
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!cur[parts[i]] || typeof cur[parts[i]] !== 'object') cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return clone;
}

export default function BustePagaImportPage() {
  const [list, setList] = useState([]);
  const [operai, setOperai] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewData, setReviewData] = useState(null);  // { file_id, filename, dati }
  const [selectedOperaio, setSelectedOperaio] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => { init(); }, []);
  const init = async () => {
    try {
      const [rl, ru] = await Promise.all([
        axios.get(`${API}/buste-paga-import`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setList(rl.data || []);
      setOperai((ru.data || []).filter(u => u.role === 'operaio'));
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };

  const onUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Solo PDF supportati'); return; }
    setUploading(true);
    toast.loading('Estrazione AI in corso... (10-30 secondi)', { id: 'upl' });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/buste-paga-import/extract`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.dismiss('upl');
      toast.success('Dati estratti — verifica e conferma');
      setReviewData(r.data);
      setSelectedOperaio('');
      setReviewOpen(true);
    } catch (err) {
      toast.dismiss('upl');
      toast.error(err?.response?.data?.detail || 'Errore estrazione AI');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const saveReview = async () => {
    if (!reviewData) return;
    try {
      await axios.post(`${API}/buste-paga-import/save`, {
        file_id: reviewData.file_id,
        dati: reviewData.dati,
        operaio_id: selectedOperaio,
      }, { withCredentials: true });
      const periodo = reviewData.dati?.periodo || {};
      const anno = periodo.anno || (reviewData.dati?.data_pagamento || '').slice(0, 4);
      if (anno) {
        toast.success(`Busta paga salvata — inclusa nel Bilancio ${anno} (B.9)`);
      } else {
        toast.success('Busta paga salvata (⚠️ periodo mancante → non entra automaticamente nel bilancio)');
      }
      setReviewOpen(false);
      setReviewData(null);
      init();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore salvataggio'); }
  };

  const del = async (b) => {
    if (!window.confirm(`Eliminare la busta paga di ${b.dati?.nome_dipendente || 'sconosciuto'}?`)) return;
    try { await axios.delete(`${API}/buste-paga-import/${b.id}`, { withCredentials: true }); toast.success('Eliminata'); init(); }
    catch { toast.error('Errore'); }
  };

  const updateField = (path, val) => setReviewData(rd => ({ ...rd, dati: setDeep(rd.dati, path, val) }));

  const confidence = reviewData?.dati?.confidence || 0;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="buste-paga-import-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Import Buste Paga (AI)</h1>
        <p className="text-slate-500 mt-1">Carica un PDF di busta paga: l&apos;AI estrae automaticamente nome, importi, ore, contributi</p>
        <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
          <Sparkles className="h-3 w-3" />
          Le buste paga salvate popolano automaticamente il <a href="/admin/bilancio" className="underline">Bilancio d&apos;Esercizio</a> (Conto Economico voce B.9 Costi del personale)
        </p>
      </div>

      <Card className="border-2 border-dashed border-slate-300 bg-slate-50/50">
        <CardContent className="p-8 text-center">
          <Upload className="h-12 w-12 mx-auto text-slate-400 mb-3" />
          <p className="text-slate-700 font-medium mb-2">Carica una busta paga PDF</p>
          <p className="text-xs text-slate-500 mb-4">L&apos;AI leggerà il documento e ti mostrerà i dati per la verifica prima del salvataggio</p>
          <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={onUpload} data-testid="busta-paga-upload-input" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-slate-900 hover:bg-slate-800" data-testid="busta-paga-upload-btn">
            <Sparkles className="h-4 w-4 mr-1.5" />
            {uploading ? 'Estrazione in corso...' : 'Seleziona PDF e estrai dati'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Buste paga importate</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Dipendente</TableHead>
                <TableHead>Periodo</TableHead>
                <TableHead className="text-right">Lordo</TableHead>
                <TableHead className="text-right">Netto</TableHead>
                <TableHead className="text-right">Contributi INPS</TableHead>
                <TableHead>Confidence AI</TableHead>
                <TableHead>In Bilancio</TableHead>
                <TableHead>PDF</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500"><FileText className="h-8 w-8 mx-auto text-slate-300 mb-2" />Nessuna busta paga importata. Carica il primo PDF sopra.</TableCell></TableRow>
              ) : list.map(b => {
                const d = b.dati || {};
                const p = d.periodo || {};
                const conf = d.confidence || 0;
                // Periodo effettivo (fallback su data_pagamento)
                let annoEffettivo = p.anno;
                let meseEffettivo = p.mese;
                if ((!annoEffettivo || !meseEffettivo) && d.data_pagamento && d.data_pagamento.length >= 7) {
                  annoEffettivo = parseInt(d.data_pagamento.slice(0, 4), 10) || annoEffettivo;
                  meseEffettivo = parseInt(d.data_pagamento.slice(5, 7), 10) || meseEffettivo;
                }
                const inBilancio = annoEffettivo && meseEffettivo;
                return (
                  <TableRow key={b.id} data-testid={`bp-row-${b.id}`}>
                    <TableCell className="font-medium">{d.nome_dipendente || '—'}</TableCell>
                    <TableCell>{meseEffettivo ? `${String(meseEffettivo).padStart(2, '0')}/${annoEffettivo}` : '—'}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(d.importo_lordo)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-emerald-700">{fmt(d.importo_netto)}</TableCell>
                    <TableCell className="text-right font-mono text-slate-500">{fmt(d.contributi_inps_dipendente)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={conf > 0.85 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : conf > 0.6 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}>{(conf * 100).toFixed(0)}%</Badge>
                    </TableCell>
                    <TableCell>
                      {inBilancio ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs" title={`Confluisce nel Bilancio ${annoEffettivo} B.9`}>✓ {annoEffettivo}</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs" title="Periodo mancante — non entra nel bilancio">Escluso</Badge>
                      )}
                    </TableCell>
                    <TableCell><a href={`${process.env.REACT_APP_BACKEND_URL}${b.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">Apri PDF</a></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => del(b)} data-testid={`delete-bp-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bp-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-blue-600" /> Verifica dati estratti dall&apos;AI</DialogTitle>
          </DialogHeader>
          {reviewData && (
            <div className="space-y-3">
              <div className={`p-2 rounded text-xs flex items-center gap-2 ${confidence > 0.85 ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : confidence > 0.6 ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                <AlertCircle className="h-3.5 w-3.5" />
                <span>Confidence AI: <strong>{(confidence * 100).toFixed(0)}%</strong>. {confidence > 0.85 ? 'Ottima estrazione — verifica solo i valori critici.' : confidence > 0.6 ? 'Verifica attentamente tutti i campi.' : 'Estrazione incerta — molto probabilmente da correggere.'}</span>
              </div>
              <div>
                <Label>Collega ad operaio (opzionale)</Label>
                <Select value={selectedOperaio || '__none__'} onValueChange={v => setSelectedOperaio(v === '__none__' ? '' : v)}>
                  <SelectTrigger data-testid="bp-select-operaio"><SelectValue placeholder="Nessun operaio" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nessun operaio —</SelectItem>
                    {operai.map(o => <SelectItem key={o.id} value={o.id}>{o.name} · {o.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {CAMPI.map(c => {
                  const v = getDeep(reviewData.dati, c.key);
                  return (
                    <div key={c.key}>
                      <Label className="text-xs">{c.label}</Label>
                      <Input
                        type={c.type === 'number' || c.type === 'euro' ? 'number' : c.type}
                        step={c.type === 'euro' ? '0.01' : c.type === 'number' ? 'any' : undefined}
                        value={v == null ? '' : v}
                        onChange={e => {
                          let val = e.target.value;
                          if (c.type === 'number' || c.type === 'euro') val = val === '' ? null : parseFloat(val);
                          updateField(c.key, val);
                        }}
                        className={v == null ? 'bg-red-50' : ''}
                        data-testid={`bp-field-${c.key.replace(/\./g, '-')}`}
                      />
                    </div>
                  );
                })}
              </div>
              {reviewData.dati?.note_extra && (
                <div className="text-xs text-slate-500 italic border-l-2 border-slate-200 pl-2">Note AI: {reviewData.dati.note_extra}</div>
              )}
              <a href={`${process.env.REACT_APP_BACKEND_URL}${reviewData.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs underline">Apri PDF originale per confronto</a>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Annulla</Button>
            <Button onClick={saveReview} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bp-review-save"><Check className="h-4 w-4 mr-1.5" />Salva busta paga</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
